import type { SiteNavigationItem } from "./siteBuilderTypes";

export const LAGUNA_CLASSIC_VISIBLE_COLUMN_LIMIT = 4;
export const LAGUNA_CLASSIC_OVERFLOW_COLUMN_LIMIT = 10;
export const LAGUNA_CLASSIC_LINK_GROUP_SIZE = 6;

export type SiteNavigationLagunaClassicColumn = Readonly<{
    key: string;
    links: readonly SiteNavigationItem[];
    title?: string;
    titleItem?: SiteNavigationItem;
}>;

/**
 * Recreates Laguna's original mega-menu column model. Ordinary categories
 * stay in one visual column (and are split into groups of six by the view),
 * while store-list entries keep their titled child columns and a leading
 * recommendation column for direct links.
 */
export function siteNavigationLagunaClassicColumns(
    item: SiteNavigationItem,
    recommendedTitle: string,
): readonly SiteNavigationLagunaClassicColumn[] {
    if (item.type !== "storeList") {
        return item.children.length > 0
            ? [{ key: `${item.uuid}:categories`, links: item.children }]
            : [];
    }

    const directLinks = item.children.filter((child) => child.children.length === 0);
    const childrenWithChildren = item.children.filter((child) => child.children.length > 0);

    if (childrenWithChildren.length === 0) {
        return item.children.length > 0
            ? [{ key: `${item.uuid}:children`, links: item.children, title: item.label }]
            : [];
    }

    return [
        ...(directLinks.length > 0 ? [{
            key: `${item.uuid}:recommended`,
            links: directLinks,
            title: recommendedTitle,
        }] : []),
        ...childrenWithChildren.map((child) => ({
            key: child.uuid,
            links: child.children,
            title: child.label,
            titleItem: child,
        })),
    ];
}

export function siteNavigationLagunaClassicLinkGroups(
    links: readonly SiteNavigationItem[],
    groupSize = LAGUNA_CLASSIC_LINK_GROUP_SIZE,
): readonly (readonly SiteNavigationItem[])[] {
    const normalizedGroupSize = Math.max(1, Math.floor(groupSize));
    const groups: SiteNavigationItem[][] = [];

    for (let index = 0; index < links.length; index += normalizedGroupSize) {
        groups.push(links.slice(index, index + normalizedGroupSize));
    }

    return groups;
}

/** Keep Laguna's sale link accent without coupling the shared renderer to shop routes. */
export function siteNavigationLagunaClassicSaleItem(item: SiteNavigationItem): boolean {
    const searchableValue = `${item.label} ${item.href ?? ""}`.toLowerCase();

    return searchableValue.includes("%")
        || [
            "sale",
            "vyprodej",
            "výprodej",
            "vypredaj",
            "angebote",
            "rebajas",
            "wyprzedaż",
            "wyprzedaz",
            "akció",
            "akcio",
            "reduceri",
        ].some((term) => searchableValue.includes(term));
}

export function siteNavigationLagunaClassicRoot(
    items: readonly SiteNavigationItem[],
    activeItemUuid?: string,
): SiteNavigationItem | null {
    const containsActiveItem = (item: SiteNavigationItem): boolean => (
        item.uuid === activeItemUuid
        || item.children.some(containsActiveItem)
    );

    return items.find(containsActiveItem)
        ?? items[0]
        ?? null;
}
